/**
 * Renewals workspace (added — see docs/BENCHMARKS.md).
 *
 * The spec puts renewals behind a filter on the policy list. Every agency-management
 * product reviewed instead gives renewals their own board, because it is the single
 * recurring workflow that drives retention: the question is not "which policies exist"
 * but "what must I call today, and what is slipping".
 *
 * Columns are urgency bands rather than pipeline stages — the band is derived from the
 * end date, so nothing has to be dragged or manually kept up to date.
 */

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Phone, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  addDays,
  daysUntil,
  formatPolicyRef,
  isRenewalDue,
  type IsoDate,
  type PolicyDto,
} from '@ias/shared';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/misc';
import { ConfirmDialog } from '@/components/common/confirm-dialog';
import { EnumBadge } from '@/components/common/enum-badge';
import { PolicyRef } from '@/components/common/policy-ref';
import { EmptyState, PageHeader } from '@/components/common/page-header';
import { ApiRequestError } from '@/lib/api/client';
import { useUpcomingRenewals, useUpdateRenewalNotice } from '@/lib/api/domains';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { useOfficeToday } from '@/lib/today';
import { cn } from '@/lib/utils';

import { DaysBadge } from './policies/list';
import { MarkPaidDialog } from './policies/mark-paid-dialog';

/** How far back the lapsed column reaches. Mirrors RENEWAL_GRACE_DAYS on the server. */
const GRACE_DAYS = 30;

interface Band {
  key: string;
  labelKey: 'renewals.lapsed' | 'renewals.critical' | 'renewals.soon' | 'renewals.upcoming';
  tone: 'critical' | 'serious' | 'warning';
  accent: string;
  /**
   * The band's window as day offsets from today, inclusive at both ends.
   *
   * One declaration for three things that have to agree: which cards land in the column,
   * the «0–6 ημ.» caption, and the dates that caption now spells out. They were three
   * separate literals, which is two chances for a column to say one thing and hold another.
   */
  window: readonly [number, number];
}

const BANDS: Band[] = [
  { key: 'lapsed', labelKey: 'renewals.lapsed', tone: 'critical', accent: 'bg-critical', window: [-GRACE_DAYS, -1] },
  { key: 'critical', labelKey: 'renewals.critical', tone: 'critical', accent: 'bg-critical', window: [0, 6] },
  { key: 'soon', labelKey: 'renewals.soon', tone: 'serious', accent: 'bg-serious', window: [7, 14] },
  { key: 'upcoming', labelKey: 'renewals.upcoming', tone: 'warning', accent: 'bg-warning', window: [15, 30] },
];

const inBand = (band: Band, days: number) => days >= band.window[0] && days <= band.window[1];

/** «22/09». Both locales the app ships put the day first, and a column heading has no room
 *  for a year — the cards below each carry the full date. */
const dayMonth = (iso: IsoDate) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

/**
 * The caption under a column heading: the window in days, and the dates it resolves to today.
 *
 * The days stay, because they are the rule the column is built on. The dates are added because
 * the rule is not what the office is holding — a certificate on the desk says 28/09, and
 * «0–6 ημ.» asked whoever is holding it to subtract today from that to find out which column
 * to look in. It rolls over at midnight in Athens, not in UTC, because `today()` does.
 *
 * Lapsed counts backwards: «1–30 ημ.» means days SINCE expiry, so its ends swap and drop their
 * sign, while the dates it prints still run earliest-first.
 */
function bandCaption(band: Band, todayIso: IsoDate, daysShort: string): string {
  const [from, to] = band.window;
  const days = from < 0 ? `${Math.abs(to)}–${Math.abs(from)}` : `${from}–${to}`;
  return `${days} ${daysShort} · ${dayMonth(addDays(todayIso, from))} – ${dayMonth(addDays(todayIso, to))}`;
}

export function RenewalsPage() {
  const { t, money, date } = useI18n();
  const navigate = useNavigate();
  const { can } = useAuth();

  /*
   * Read from the hook, not from `today()` at render: the columns are a statement about
   * which dates they cover, and a board left open overnight would keep yesterday's.
   */
  const todayIso = useOfficeToday();

  const { data, isLoading } = useUpcomingRenewals(30, 200);
  const [paying, setPaying] = React.useState<PolicyDto | null>(null);
  const [declining, setDeclining] = React.useState<PolicyDto | null>(null);
  /*
   * Why the client is leaving — kept, because «δεν ανανεώνει» without a reason is a dead end a
   * year later («πήγε αλλού φθηνότερα» and «πούλησε το αυτοκίνητο» are different calls). It is
   * the notice note the Ειδοποιητήρια dialog already writes, prefilled so nothing is lost, and
   * every change to it lands in the policy's history.
   */
  const [declineReason, setDeclineReason] = React.useState('');
  React.useEffect(() => setDeclineReason(declining?.noticeNote ?? ''), [declining]);
  const updateNotice = useUpdateRenewalNotice();

  /*
   * «Δεν ανανεώνει» is the notice status the Ειδοποιητήρια dialog already records; the server
   * drops declined policies from this board (D14), so the card leaves once the query refetches.
   * The reason typed in the dialog is the note — the endpoint writes whatever note it receives.
   */
  const confirmDecline = () => {
    if (!declining) return;
    updateNotice.mutate(
      { policyId: declining.id, noticeStatus: 'declined', noticeNote: declineReason.trim() || null },
      {
        onSuccess: () => setDeclining(null),
        onError: (caught) =>
          toast.error(caught instanceof ApiRequestError ? caught.message : t('error.generic')),
      },
    );
  };

  const grouped = React.useMemo(() => {
    const result: Record<string, PolicyDto[]> = Object.fromEntries(BANDS.map((band) => [band.key, []]));
    for (const policy of data ?? []) {
      const days = daysUntil(policy.endDate, todayIso);
      const band = BANDS.find((candidate) => inBand(candidate, days));
      if (band) result[band.key]!.push(policy);
    }
    return result;
  }, [data, todayIso]);

  const totalPremium = (policies: PolicyDto[]) =>
    policies.reduce((sum, policy) => sum + Number(policy.premiumAmount), 0);

  return (
    <>
      <PageHeader
        title={t('renewals.title')}
        description={t('renewals.subtitle')}
        actions={
          <Button variant="outline" onClick={() => navigate('/policies?status=renewal_due')}>
            {t('policy.title')}
          </Button>
        }
      />

      <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-4">
        {BANDS.map((band) => {
          const policies = grouped[band.key] ?? [];

          return (
            <section key={band.key} className="flex min-w-0 flex-col">
              <div className="mb-3">
                <div className="flex items-center gap-2">
                  <span className={cn('size-2 shrink-0 rounded-full', band.accent)} aria-hidden />
                  <h2 className="text-sm font-semibold">{t(band.labelKey)}</h2>
                  <Badge tone="outline" size="sm" className="tabular">
                    {policies.length}
                  </Badge>
                  {policies.length > 0 && (
                    <span className="text-muted-soft tabular ml-auto text-xs">
                      {money(totalPremium(policies))}
                    </span>
                  )}
                </div>
                {/* Each column states its own window, in days and in dates. The lapsed one
                    has a 30-day win-back limit that was previously invisible, so an older
                    expiry looked like a missing record rather than an out-of-scope one —
                    and «0–6 ημ.» still left the office counting on its fingers to find out
                    whether a certificate on the desk belonged in this column. */}
                <p className="text-muted-soft mt-1 pl-4 text-[11px] tabular">
                  {bandCaption(band, todayIso, t('common.daysShort'))}
                </p>
              </div>

              <div className="space-y-2">
                {isLoading ? (
                  Array.from({ length: 3 }).map((_, index) => <Skeleton key={index} className="h-24 w-full" />)
                ) : !policies.length ? (
                  /*
                   * Two shapes for one message, because the bands are side by side on a wide
                   * screen and stacked on a narrow one. As a column among four, an empty card
                   * holds its place in the row and reads as "nothing here" — worth seeing.
                   * Stacked on a phone it becomes a full card of nothing that has to be
                   * scrolled past to reach actual work, so below `lg` the same fact is stated
                   * in one quiet line instead.
                   */
                  <>
                    <p className="text-muted-soft px-1 py-1 text-xs lg:hidden">{t('renewals.empty')}</p>
                    <Card className="hidden lg:flex">
                      <CardContent className="px-3 py-6">
                        <EmptyState title={t('renewals.empty')} compact className="py-0" />
                      </CardContent>
                    </Card>
                  </>
                ) : (
                  policies.map((policy) => {
                    const days = daysUntil(policy.endDate, todayIso);
                    const plates = policy.plates ?? [];
                    return (
                      <Card
                        key={policy.id}
                        className="hover:border-primary/40 cursor-pointer transition-colors"
                        onClick={() => navigate(`/policies/${policy.id}`)}
                      >
                        <CardContent className="space-y-2 px-3.5 py-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="truncate text-[13px] font-medium">{policy.clientName}</p>
                              {/* The reasoning this row pioneered — plate first, because that is
                                  what the office says out loud — now lives in `PolicyRef` and is
                                  applied everywhere a policy is named. */}
                              <PolicyRef
                                policyNumber={policy.policyNumber}
                                plates={plates}
                                className="text-muted-foreground text-xs"
                                muted
                              />
                              {policy.insuranceCompanyName && (
                                <p className="text-muted-foreground truncate text-xs">
                                  {policy.insuranceCompanyName}
                                </p>
                              )}
                            </div>
                            {/* Was an inline badge that spelled the same figure differently
                                from DaysBadge («0 ημ.» vs «0»). One formatter now, so
                                «Σήμερα» cannot appear on the policy page and not here. */}
                            <DaysBadge days={days} tone={band.tone} withUnit />
                          </div>

                          <div className="flex items-center justify-between gap-2">
                            <EnumBadge enum="insuranceType" value={policy.insuranceType} size="sm" />
                            <span className="text-sm font-semibold tabular">{money(policy.premiumAmount)}</span>
                          </div>

                          <div className="flex items-center justify-between gap-2 border-t pt-2">
                            <span className="text-muted-foreground text-[11px] tabular">
                              {date(policy.endDate)}
                            </span>
                            <div
                              className="flex items-center gap-0.5"
                              onClick={(event) => event.stopPropagation()}
                            >
                              {policy.clientId && (
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  onClick={() => navigate(`/clients/${policy.clientId}`)}
                                  aria-label={t('client.one')}
                                >
                                  <Phone />
                                </Button>
                              )}
                              {/*
                                Was `paymentStatus !== 'paid'`, which hid this button on
                                exactly the policies most in need of it: collected, past
                                their end date, and never rolled forward. The shared
                                predicate asks the whole question — money to collect OR a
                                period to advance — so this board and the policy page
                                cannot drift apart on it again.
                              */}
                              {can('policy:renew') && isRenewalDue(policy) && (
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  onClick={() => setPaying(policy)}
                                  aria-label={
                                    policy.paymentStatus === 'paid'
                                      ? t('action.renew')
                                      : t('action.markPaid')
                                  }
                                >
                                  <CheckCircle2 className="text-good" />
                                </Button>
                              )}
                              {can('policy:write') && (
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  onClick={() => setDeclining(policy)}
                                  aria-label={t('renewals.decline')}
                                  title={t('renewals.decline')}
                                >
                                  <X className="text-critical" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </div>
            </section>
          );
        })}
      </div>

      {paying && <MarkPaidDialog policy={paying} open onOpenChange={(open) => !open && setPaying(null)} />}
      <ConfirmDialog
        open={declining !== null}
        onOpenChange={(open) => !open && !updateNotice.isPending && setDeclining(null)}
        title={t('renewals.declineTitle')}
        subject={declining ? `${declining.clientName} · ${formatPolicyRef(declining.policyNumber, declining.plates)}` : undefined}
        description={t('renewals.declineHint')}
        confirmLabel={t('renewals.decline')}
        onConfirm={confirmDecline}
        loading={updateNotice.isPending}
      >
        <div className="space-y-1.5">
          <Label htmlFor="declineReason">{t('renewals.declineReason')}</Label>
          <Textarea
            id="declineReason"
            rows={3}
            value={declineReason}
            onChange={(event) => setDeclineReason(event.target.value)}
            placeholder={t('renewals.declineReasonPlaceholder')}
          />
        </div>
      </ConfirmDialog>
    </>
  );
}
